import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PropertyFilter as PropertyFilterComponent } from "@amzn/awsui-components-react";
import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris/property-filter/interfaces";
import {
  PropertyFilterQuery,
  PropertyFilterToken,
} from "@amzn/awsui-collection-hooks/cjs/interfaces";
import { RootState } from "../app/rootReducer";
import {
  PropertyFilter,
  PropertyFilterGroup,
  flattenEnumOrGroupsInternal,
  resetActivePropertyFilters,
  setActivePropertyFilter,
  setQueryPropertyFilters,
  setQueryPropertyFiltersForceRehydrate,
} from "../searchSideBar/searchSideBarConnector";
import {
  MULTISELECT_PROPERTY_KEYS,
  PROPERTY_FILTER_I18N,
  propertyFiltersConfig,
} from "../searchSideBar/searchPropertyFilter/SearchPropertyFilter-config";
import { DISABLED_SELECT_ALL_GROUP_LABELS } from "../searchSideBar/searchPropertyFilter/multiSelectPropertyKeys";
import {
  buildCustomGroupsText,
  buildGroupedFilteringProperties,
} from "../searchSideBar/searchPropertyFilter/SearchPropertyFilter-groups";
import { genericStringSplitter } from "../../util/stringUtils";
import "../searchSideBar/searchPropertyFilter/SearchPropertyFilter.css";
import { mergeWithPreservedAsin } from "./mergeWithPreservedAsin";

/**
 * Promote free-text tokens (no `propertyKey`) to ASIN tokens, so
 * pasted ASINs land in the right filter without picking a property.
 * `any` to sidestep flaky `@amzn/awsui-collection-hooks` types.
 */
function promoteFreeTextTokensToAsin(query: any): any {
  const promote = (token: any): any => {
    if (!token || typeof token !== "object") return token;
    if (token.propertyKey) return token;
    if (typeof token.value !== "string" || token.value.trim() === "") {
      return token;
    }
    return {
      propertyKey: "asin",
      value: token.value,
      operator: "=",
    };
  };

  const walkGroup = (group: any): any => {
    if (!group || typeof group !== "object") return group;
    return {
      ...group,
      tokens: (group.tokens ?? []).map((t: any) =>
        t?.operation ? walkGroup(t) : promote(t)
      ),
      tokenGroups: (group.tokenGroups ?? []).map((tg: any) =>
        tg?.operation ? walkGroup(tg) : promote(tg)
      ),
    };
  };

  return walkGroup(query);
}

/**
 * Merge root-level tokens sharing the same `(propertyKey, operator)`
 * into a single OR tokenGroup, and dedup identical values (compared
 * by JSON stringification so date-range objects work too).
 *
 * Fixes two UX papercuts on the shared filter bar:
 *   - Adding ASIN B001 then ASIN B002 as separate chips produced
 *     `asin=B001 AND asin=B002`, which matches nothing.
 *   - Re-adding an ASIN or marketplace value that was already picked
 *     produced a duplicate chip that widens the query for no reason.
 *
 * Restricted to the `=` operator (all configured properties use it)
 * so we don't rewrite `!=` / `<` / `>` chips where AND is correct.
 * Preserves nested non-OR groups and tokens with other operators.
 */
function coalesceSamePropertyTokens(query: any): any {
  // Root items may live in either `tokens` or `tokenGroups` depending
  // on how Polaris emits them. Existing helpers (promote/split) walk
  // both, so we do the same and re-emit exclusively into tokenGroups
  // — the shape `preProcessPolarisPropertyFilter` expects at root.
  const rootItems: any[] = [
    ...(query?.tokens ?? []),
    ...(query?.tokenGroups ?? []),
  ];
  const buckets = new Map<
    string,
    { propertyKey: string; operator: string; values: any[] }
  >();
  const passthrough: any[] = [];

  const valueKey = (v: any): string =>
    typeof v === "string" ? v : JSON.stringify(v);

  const bucketFor = (propertyKey: string, operator: string) => {
    const k = `${propertyKey}|${operator}`;
    let b = buckets.get(k);
    if (!b) {
      b = { propertyKey, operator, values: [] };
      buckets.set(k, b);
    }
    return b;
  };

  // Collects whole enum (array-value) tokens per property, so
  // individually-added same-property enum tokens become ONE or-group
  // of pills (outer OR) while each token keeps its `string[]` value
  // (inner comma display). Keyed by propertyKey.
  const enumGroups = new Map<string, any[]>();
  const enumGroupFor = (propertyKey: string) => {
    let g = enumGroups.get(propertyKey);
    if (!g) {
      g = [];
      enumGroups.set(propertyKey, g);
    }
    return g;
  };

  for (const item of rootItems) {
    if (item?.operation === "or") {
      const orTokens: any[] = item.tokens ?? [];
      if (orTokens.length === 0) continue;
      const first = orTokens[0];
      const uniform = orTokens.every(
        (t) =>
          !t?.operation &&
          t?.propertyKey === first?.propertyKey &&
          t?.operator === first?.operator &&
          t?.operator === "="
      );
      if (uniform && first?.propertyKey) {
        // A uniform same-property OR-group. Its children may be enum
        // array tokens (value:["x"]) — e.g. a previously-applied enum
        // group coming back through the round-trip — or plain scalar
        // tokens. Route array-valued children into `enumGroups` (the
        // same collector new enum tokens use) so a later individual
        // add reunites with them instead of landing in a separate
        // `buckets` group and AND-ing against them. Scalar children go
        // to the scalar bucket as before.
        const childrenAreEnum = orTokens.every((t) => Array.isArray(t?.value));
        if (
          childrenAreEnum ||
          MULTISELECT_PROPERTY_KEYS.has(first.propertyKey)
        ) {
          const g = enumGroupFor(first.propertyKey);
          for (const t of orTokens) g.push(t);
        } else {
          const b = bucketFor(first.propertyKey, first.operator);
          for (const t of orTokens) b.values.push(t.value);
        }
        continue;
      }
      passthrough.push(item);
    } else if (item?.operation) {
      // Nested AND (or unexpected) group — leave alone.
      passthrough.push(item);
    } else if (item?.propertyKey && item?.operator === "=") {
      // Enum multi-select tokens carry an array value (string[] value).
      // Collect same-property tokens so they group under one OR (outer pills);
      // each token keeps its array value (inner comma display).
      if (Array.isArray(item.value)) {
        enumGroupFor(item.propertyKey).push(item);
        continue;
      }
      const b = bucketFor(item.propertyKey, item.operator);
      b.values.push(item.value);
    } else {
      passthrough.push(item);
    }
  }

  const merged: any[] = [...passthrough];
  for (const bucket of buckets.values()) {
    const seen = new Set<string>();
    const deduped: any[] = [];
    for (const v of bucket.values) {
      const k = valueKey(v);
      if (seen.has(k)) continue;
      seen.add(k);
      deduped.push(v);
    }
    if (deduped.length === 1) {
      merged.push({
        propertyKey: bucket.propertyKey,
        value: deduped[0],
        operator: bucket.operator,
      });
    } else {
      merged.push({
        operation: "or",
        tokens: deduped.map((v) => ({
          propertyKey: bucket.propertyKey,
          value: v,
          operator: bucket.operator,
        })),
      });
    }
  }

  // Emit collected enum values, ONE merged `string[]` token per
  // property, so same-property values always render as a single
  // multi-select chip (checkbox popover) — never as a dangling
  // `or Program = X` pill beside a `Program = [A, B]` chip. This
  // matches what picking several values together from the dropdown
  // produces. Flattens array-valued and scalar children alike and
  // dedupes per value. Different properties never share a group, so
  // they stay separate chips and combine as AND. Downstream, each
  // string[] token expands to `or(filter(k,v), ...)`.
  for (const tokens of enumGroups.values()) {
    const seen = new Set<string>();
    const values: any[] = [];
    for (const t of tokens) {
      const arr = Array.isArray(t.value) ? t.value : [t.value];
      for (const v of arr) {
        const k = valueKey(v);
        if (seen.has(k)) continue;
        seen.add(k);
        values.push(v);
      }
    }
    if (values.length === 0) continue;
    const first = tokens[0];
    merged.push({
      propertyKey: first.propertyKey,
      value: values,
      operator: first.operator ?? "=",
    });
  }

  return {
    ...query,
    tokens: [],
    tokenGroups: merged,
  };
}

/**
 * Split multi-value ASIN tokens (comma / whitespace / tab / newline
 * per `genericStringSplitter`) into an OR `tokenGroup` of single-ASIN
 * tokens so chips display split without a URL round-trip.
 */
function splitCommaSeparatedAsinTokens(query: any): any {
  const splitToken = (token: any): any => {
    if (token?.propertyKey !== "asin") return token;
    const raw = typeof token.value === "string" ? token.value : "";
    const parts = genericStringSplitter(raw);
    if (parts.length <= 1) return token;
    return {
      operation: "or",
      tokens: parts.map((v) => ({
        propertyKey: "asin",
        value: v,
        operator: token.operator,
      })),
    };
  };

  const walkGroup = (group: any): any => {
    if (!group || typeof group !== "object") return group;
    return {
      ...group,
      tokens: (group.tokens ?? []).map((t: any) =>
        t?.operation ? walkGroup(t) : splitToken(t)
      ),
      tokenGroups: (group.tokenGroups ?? []).map((tg: any) =>
        tg?.operation ? walkGroup(tg) : splitToken(tg)
      ),
    };
  };

  return walkGroup(query);
}

/** Polaris query shape with the recursive `tokenGroups` field. */
interface PolarisPropertyFilterGroup extends PropertyFilterQuery {
  tokenGroups?: PolarisPropertyFilterGroup[];
}

export interface PropertyFilterInputProps {
  /**
   * Property keys hidden from the "add filter" dropdown. Legacy passes
   * `["asin"]` because it owns ASIN via a dedicated textarea; App
   * Shell passes nothing. Existing tokens still round-trip.
   */
  omitProperties?: string[];
  /**
   * Enable Cloudscape's free-text input AND auto-promote property-less
   * tokens to ASIN. Default false so Legacy (textarea-owned ASIN) is
   * untouched.
   */
  enableFreeTextAsin?: boolean;
  /**
   * Cloudscape PropertyFilter `customControl` slot — rendered above
   * the filter input (e.g. Saved filter sets Select).
   */
  customControl?: React.ReactNode;
  /**
   * Cloudscape PropertyFilter `customFilterActions` slot — rendered
   * alongside the "Clear filters" button below the tokens (e.g.
   * filter set actions dropdown).
   */
  customFilterActions?: React.ReactNode;
  /**
   * Cloudscape `tokenLimit` — the maximum number of top-level chips
   * shown before a "Show more / Show fewer" toggle collapses the
   * rest. Caps how tall the filter bar can grow when many ASIN (or
   * other) chips are applied. Omit to show all chips (default).
   */
  tokenLimit?: number;
}

/**
 * Shared property-filter input. Owns the two-way translation between
 * the Polaris query and this slice's `PropertyFilterGroup`. Preserves
 * any pre-existing top-level `asin` PropertyFilter when writing back
 * (Legacy textarea / URL keeps ownership).
 */
export const PropertyFilterInput: React.FC<PropertyFilterInputProps> = ({
  omitProperties,
  enableFreeTextAsin = false,
  customControl,
  customFilterActions,
  tokenLimit,
}) => {
  const dispatch = useDispatch();

  // Container ref used to scope DOM enhancements (group-clear buttons
  // injected via MutationObserver below) to this instance only —
  // multiple PropertyFilterInputs on one page must not affect each
  // other's chips.
  const containerRef = React.useRef<HTMLDivElement>(null);

  const { productLinesData } = useSelector(
    (state: RootState) => state.productLines
  );
  const { programNameData } = useSelector(
    (state: RootState) => state.upstreamAttributesData
  );
  const {
    queryPropertyFilters,
    queryPropertyFiltersForceRehydrate,
    queryPropertyFiltersRehydrateSignal,
  } = useSelector((state: RootState) => state.generateQuery);

  const [polarisPropertyFilter, setPolarisPropertyFilter] =
    useState<PolarisPropertyFilterGroup>({
      tokens: [],
      tokenGroups: [],
      operation: "and",
    });

  // Full config: used for validation so omitted-property tokens
  // (e.g. ASIN in Legacy) still round-trip through the slice.
  const polarisPropertyFilterConfig = useMemo(
    () => propertyFiltersConfig(productLinesData, programNameData),
    [productLinesData, programNameData]
  );

  // Config minus `omitProperties`, feeds the dropdown UI only.
  const uiFilterableConfig = useMemo(() => {
    if (!omitProperties || omitProperties.length === 0) {
      return polarisPropertyFilterConfig;
    }
    const omit = new Set(omitProperties);
    return Object.fromEntries(
      Object.entries(polarisPropertyFilterConfig).filter(
        ([key]) => !omit.has(key)
      )
    );
  }, [polarisPropertyFilterConfig, omitProperties]);

  // Ordered, grouped properties for the dropdown. Group membership and
  // per-group ordering come from `PROPERTY_FILTER_GROUPS` so they can be
  // edited without touching this component.
  const groupedFilteringProperties = useMemo(
    () =>
      buildGroupedFilteringProperties(
        Object.values(uiFilterableConfig).map((p) => p.filteringProperty)
      ),
    [uiFilterableConfig]
  );

  // Block keyboard select-all (Enter/Space) on the Program / Product Line group
  // headers — the backend can't serve that large a select-all query.
  useEffect(() => {
    const disabled_select_all = new Set<string>(
      DISABLED_SELECT_ALL_GROUP_LABELS
    );
    const block = (e: KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const activeId = (e.target as HTMLElement)?.getAttribute?.(
        "aria-activedescendant"
      );
      if (!activeId) return;
      const row = document.getElementById(activeId);
      // Only the group-parent header row, never a value row.
      if (!row?.matches('[class*="awsui_parent"]')) return;
      const title = row.querySelector("[title]")?.getAttribute("title") ?? "";
      if (disabled_select_all.has(title)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener("keydown", block, true);
    return () => document.removeEventListener("keydown", block, true);
  }, []);

  const customGroupsText = useMemo(() => buildCustomGroupsText(), []);

  // Latest state exposed to the DOM click handler installed once
  // below. Cloudscape re-renders the token list on every query
  // change, tearing down and rebuilding chip DOM; we can't rebind
  // effects on every render (would leak observers), so the handler
  // reads the fresh query and dispatchers through a ref.
  const clearGroupStateRef = React.useRef<{
    query: PolarisPropertyFilterGroup;
    applyQuery: (next: PolarisPropertyFilterGroup) => void;
  }>({
    query: { tokens: [], tokenGroups: [], operation: "and" },
    applyQuery: () => {},
  });

  // Sync every render so the handler sees the latest state and the
  // freshly-closed `updateQueryFromPropertyFilters` /
  // `preProcessPolarisPropertyFilter` (which read Redux state — for
  // preserving the top-level asin filter and for resetting +
  // re-emitting the active-filter set, respectively).
  React.useEffect(() => {
    clearGroupStateRef.current = {
      query: polarisPropertyFilter,
      applyQuery: (next) => {
        // Mirror the Cloudscape `onChange` path so the
        // active-property-filter Redux set (which powers the "on"
        // indicators in the sidebar) stays consistent with the tokens
        // after a group is removed. Skipping this leaks stale entries
        // whenever a group is cleared via the injected button.
        const processed = preProcessPolarisPropertyFilter(next);
        setPolarisPropertyFilter(processed);
        updateQueryFromPropertyFilters(processed);
      },
    };
  });

  // Injects a "remove whole group" button next to the pencil (edit)
  // icon Cloudscape shows on parent chips of multi-token groups.
  // Cloudscape doesn't expose a group-level dismiss slot, so we
  // decorate the DOM via a MutationObserver scoped to our container
  // and dispatch removal by matching the chip's DOM position to the
  // corresponding entry in `polarisPropertyFilter.tokenGroups`
  // (tokens live only in `tokenGroups` at root — see
  // `preProcessPolarisPropertyFilter`).
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const MARKER = "data-clear-group-btn";
    const CLOSE_ICON_SVG =
      '<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" width="12" height="12"><path d="m2 1.71 12 12M2 13.71l12-12" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    // Cloudscape internals this effect depends on. Cloudscape doesn't
    // expose these as public API, so if an upgrade renames them the
    // group-clear button silently stops being injected and the user
    // falls back to removing inner tokens one at a time — nothing
    // breaks, and this is the single place to patch.
    const S = {
      // Parent chip: `awsui_filtering-token_...`, excluding the inner
      // token wrapper `awsui_filtering-token-inner_...`.
      parentChip:
        '[class*="awsui_filtering-token_"]:not([class*="filtering-token-inner"])',
      // Wrapper for a single inner token (used to detect multi-token
      // groups). Trailing underscore avoids matching sibling classes
      // like `filtering-token-inner-content` / `-dismiss-button`.
      innerToken: '[class*="filtering-token-inner_"]',
      // Pencil button Cloudscape renders as the parent action for
      // multi-token groups.
      editButton: '[class*="filtering-token-edit-button"]',
      // Popover span wrapping the edit button — target for inserting
      // our clear button as its next sibling.
      popoverWrapper: '[class*="root-filtering-token"]',
    };

    const chipStillQualifies = (chip: HTMLElement | null): boolean => {
      if (!chip) return false;
      return (
        !!chip.querySelector(S.innerToken) && !!chip.querySelector(S.editButton)
      );
    };

    const decorate = () => {
      // Prune injected buttons whose parent chip no longer qualifies
      // as a multi-token group. Cloudscape reuses chip DOM across
      // renders (React keys token items by index), so when the user
      // removes inner tokens down to one, the chip silently swaps its
      // pencil for a dismiss button but our sibling <button> — which
      // React doesn't manage — persists as a stale extra X.
      const existingBtns = container.querySelectorAll<HTMLElement>(
        `[${MARKER}]`
      );
      existingBtns.forEach((btn) => {
        const chip = btn.closest<HTMLElement>(S.parentChip);
        if (!chipStillQualifies(chip)) btn.remove();
      });

      const parents = container.querySelectorAll<HTMLElement>(S.parentChip);
      parents.forEach((chip) => {
        // Only decorate parent chips of multi-token groups. Single-
        // token chips already carry their own dismiss X.
        if (!chipStillQualifies(chip)) return;
        if (chip.querySelector(`[${MARKER}]`)) return;
        const editBtn = chip.querySelector<HTMLElement>(S.editButton);
        if (!editBtn) return;
        // The edit button is wrapped in an InternalPopover
        // (`<span class="...root-filtering-token...">`). Insert our
        // clear button as a sibling right after it so it lives inside
        // the same `.token` row and doesn't disturb the popover.
        const popoverWrapper =
          editBtn.closest<HTMLElement>(S.popoverWrapper) ??
          editBtn.parentElement;
        if (!popoverWrapper?.parentElement) return;
        const btn = document.createElement("button");
        btn.type = "button";
        btn.setAttribute(MARKER, "");
        btn.setAttribute("aria-label", "Remove all filters in group");
        btn.title = "Remove all filters in group";
        btn.style.cssText = [
          "background: transparent",
          "border: none",
          "color: inherit",
          "cursor: pointer",
          "padding: 0 4px",
          "margin-left: 2px",
          "display: inline-flex",
          "align-items: center",
          "justify-content: center",
          "opacity: 0.7",
        ].join(";");
        btn.addEventListener("mouseenter", () => {
          btn.style.opacity = "1";
        });
        btn.addEventListener("mouseleave", () => {
          btn.style.opacity = "0.7";
        });
        btn.innerHTML = CLOSE_ICON_SVG;
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const { query, applyQuery } = clearGroupStateRef.current;
          // DOM order matches root `tokenGroups` order. Re-query at
          // click time so we're using the current list (chips may
          // have been added/removed since decoration).
          const currentParents = Array.from(
            container.querySelectorAll<HTMLElement>(S.parentChip)
          );
          const idx = currentParents.indexOf(chip);
          if (idx < 0) return;
          const nextGroups = [...(query.tokenGroups ?? [])];
          if (idx >= nextGroups.length) return;
          nextGroups.splice(idx, 1);
          applyQuery({ ...query, tokenGroups: nextGroups });
        });
        popoverWrapper.parentElement.insertBefore(
          btn,
          popoverWrapper.nextSibling
        );
      });
    };

    decorate();
    const observer = new MutationObserver(() => decorate());
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Rehydrate Polaris on mount + on any rehydrate signal edge.
  //
  // We key off the monotonic `queryPropertyFiltersRehydrateSignal`
  // (not the boolean flag) so that even under React 17's unbatched
  // useEffect updates — where dispatches from two sibling components
  // in the same commit can interleave with our own consume-and-reset
  // — every explicit rehydrate request from a producer lands here as
  // a distinct edge. `queryPropertyFilters` is intentionally omitted
  // from deps: the signal is the sole trigger, and listing the tree
  // would re-fire the effect on every self-write from onChange and
  // stomp the user's in-progress typing state.
  useEffect(() => {
    rehydratePolarisPropertyFilter(queryPropertyFilters);
    if (queryPropertyFiltersForceRehydrate) {
      dispatch(setQueryPropertyFiltersForceRehydrate(false));
    }
  }, [queryPropertyFiltersRehydrateSignal]);

  const rehydratePolarisPropertyFilter = (
    propertyFilterGroup: PropertyFilterGroup
  ): void => {
    if (!propertyFilterGroup || propertyFilterGroup.filters.length === 0) {
      setPolarisPropertyFilter({ tokens: [], operation: "and" });
      return;
    }

    // Accumulate so top-level PropertyFilter(asin) AND every nested
    // attribute PropertyFilterGroup render — not just the last one.
    // The parser normalises the tree so root children are always one
    // of: an asin PropertyFilter, or a PropertyFilterGroup; the
    // forEach below drops anything else. Root arity is no longer
    // capped: URLs with several sibling attribute groups (e.g. a
    // legacy flat `and(range, or, or)` shape) emit N root PFGs, and
    // each contributes its own tokens to `merged`.
    const merged: PolarisPropertyFilterGroup = {
      tokens: [],
      tokenGroups: [],
      operation: "and",
    };

    propertyFilterGroup.filters.forEach((property) => {
      if (property.type === "PropertyFilterGroup") {
        // Fast path: an OR of same-multi-select-property scalars
        // collapses to a single enum token (e.g. `or(country=US,
        // country=CA)` → one `country=[US,CA]` chip). Mirror of the
        // same fold `transformPropertyFilterGroupToPolarisProperty
        // Filter` already applies to PFG children of a nested PFG.
        const enumToken = maybeCollapseToEnumToken(property);
        if (enumToken) {
          merged.tokenGroups!.push(enumToken);
          return;
        }
        if (property.operation === "and") {
          // "Attribute wrapper" shape — either the new-view emit's
          // single nested `and` (root child) or the parse-side range
          // wrap. Its children are per-attribute leaves/PFGs meant
          // to render as top-level chips. Transform at root=true and
          // spread so each element lands directly in
          // `merged.tokenGroups`.
          const next = transformPropertyFilterGroupToPolarisPropertyFilter(
            property,
            true
          );
          merged.tokenGroups = [
            ...(merged.tokenGroups ?? []),
            ...(next.tokenGroups ?? []),
          ];
          merged.tokens = [...(merged.tokens ?? []), ...(next.tokens ?? [])];
        } else {
          // Root-level OR PFG on a non-multiselect property (e.g. an
          // `or(programId=A, programId=B, ...)` legacy shape).
          // Transform at root=false so the group's `or` operation is
          // preserved as a nested Polaris group, and push the group
          // as one element — spreading would flatten its tokens into
          // the outer `and` and lose the OR semantics.
          const next = transformPropertyFilterGroupToPolarisPropertyFilter(
            property,
            false
          );
          merged.tokenGroups!.push(next);
        }
      } else if (
        property.type === "PropertyFilter" &&
        property.propertyKey === "asin"
      ) {
        // Legacy shape / OR-of-asins collapse: single -> one chip,
        // multi -> OR-joined tokenGroup.
        const raw = typeof property.value === "string" ? property.value : "";
        const asins = raw
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v.length > 0);
        const polarisOperator: PropertyFilterToken["operator"] =
          property.operator === "fuzzy" ? ":" : "=";
        if (asins.length === 1) {
          merged.tokenGroups!.push({
            propertyKey: "asin",
            value: asins[0],
            operator: polarisOperator,
          } as PropertyFilterToken);
        } else if (asins.length > 1) {
          merged.tokenGroups!.push({
            operation: "or",
            tokens: asins.map(
              (v) =>
                ({
                  propertyKey: "asin",
                  value: v,
                  operator: polarisOperator,
                } as PropertyFilterToken)
            ),
          } as PolarisPropertyFilterGroup);
        }
      }
    });

    setPolarisPropertyFilter(preProcessPolarisPropertyFilter(merged));
  };

  // In Legacy the ASIN textarea (outside Polaris) owns the top-level
  // `asin` PropertyFilter and passes `omitProperties=["asin"]` so ASIN
  // never appears as a Polaris chip. In App Shell no `omitProperties`
  // is passed and ASIN is a first-class Polaris chip — the Polaris
  // query is the source of truth. This flag drives the merge below:
  // preserving ASIN in App Shell would silently re-add an ASIN the
  // user just removed, leaving Submit disabled because the pending
  // query equals the applied one.
  const asinOwnedByPolaris = !omitProperties?.includes("asin");

  // Write attribute filters back. Preserve top-level `asin`
  // PropertyFilter only when it's managed outside Polaris (Legacy
  // textarea).
  const updateQueryFromPropertyFilters = (
    query: PropertyFilterProps.Query
  ): void => {
    // Flatten nested OR-of-same-enum-property chains before dispatch
    // (and thus before URL serialisation). Prevents Polaris token-
    // group arrangements — a user dropping two enum tokens for the
    // same property into a nested group — from producing URLs that
    // exceed `spreadPropertyFilterQueries`'s depth-2 limit on
    // re-parse.
    const attributeFilters = flattenEnumOrGroupsInternal(
      transformPolarisPropertyFilterToPropertyFilterGroup(query)
    );
    dispatch(
      setQueryPropertyFilters(
        mergeWithPreservedAsin(
          queryPropertyFilters,
          attributeFilters,
          asinOwnedByPolaris
        )
      )
    );
  };

  // Drop unknown properties + empty values; refresh the active-filter
  // set used elsewhere for visibility indicators.
  const preProcessPolarisPropertyFilter = (
    query: any,
    root = true
  ): PolarisPropertyFilterGroup => {
    if (root) {
      dispatch(resetActivePropertyFilters());
    }
    const subQuery: any = {
      tokens: [],
      tokenGroups: [],
      operation: query.operation ?? "and",
    };
    if (query?.operation) {
      const tokens = root ? query?.tokenGroups : query?.tokens;
      (tokens ?? []).forEach((token: any) => {
        if (token?.operation) {
          const nested = preProcessPolarisPropertyFilter(token, false);
          if (nested?.tokens.length > 0) {
            if (root) subQuery.tokenGroups.push(nested);
            else subQuery.tokens.push(nested);
          }
        } else if (
          token?.propertyKey &&
          polarisPropertyFilterConfig[token?.propertyKey]
        ) {
          if (token?.value && token?.value?.toString()?.trim() !== "") {
            if (root) subQuery.tokenGroups.push(token);
            else subQuery.tokens.push(token);
            dispatch(setActivePropertyFilter(token?.propertyKey));
          }
        }
      });
    }
    return subQuery;
  };

  const transformPolarisPropertyFilterToPropertyFilterGroup = (
    group: PolarisPropertyFilterGroup
  ): PropertyFilterGroup => {
    const propertyFilterGroup: PropertyFilterGroup = {
      type: "PropertyFilterGroup",
      filters: [],
      operation: group.operation,
    };
    [...(group?.tokens ?? []), ...(group?.tokenGroups ?? [])].forEach(
      (token: PolarisPropertyFilterGroup | PropertyFilterToken) => {
        if ("operation" in token) {
          propertyFilterGroup.filters.push(
            transformPolarisPropertyFilterToPropertyFilterGroup(token)
          );
        } else if ("propertyKey" in token) {
          propertyFilterGroup.filters.push(expandPolarisTokenOrEnum(token));
        }
      }
    );
    return propertyFilterGroup;
  };

  /**
   * Bridge Polaris enum tokens (`value: string[]`) into the scalar-
   * per-`PropertyFilter` shape the query builder and URL round-trip
   * expect: one array element → one `PropertyFilter`, many elements
   * → a `PropertyFilterGroup { operation: "or", filters: [...] }`.
   * Non-enum tokens pass through unchanged.
   */
  const expandPolarisTokenOrEnum = (
    token: PropertyFilterToken
  ): PropertyFilter | PropertyFilterGroup => {
    if (!Array.isArray(token.value)) {
      return transformPolarisPropertyFilterTokenToPropertyFilter(token);
    }
    const values: any[] = token.value;
    if (values.length === 0) {
      // Shouldn't reach here — preProcess drops empty-array tokens
      // upstream — but be defensive.
      return transformPolarisPropertyFilterTokenToPropertyFilter({
        ...token,
        value: "",
      });
    }
    if (values.length === 1) {
      return transformPolarisPropertyFilterTokenToPropertyFilter({
        ...token,
        value: values[0],
      });
    }
    return {
      type: "PropertyFilterGroup",
      operation: "or",
      filters: values.map((v) =>
        transformPolarisPropertyFilterTokenToPropertyFilter({
          ...token,
          value: v,
        })
      ),
    };
  };

  const transformPropertyFilterGroupToPolarisPropertyFilter = (
    propertyFilterGroup: PropertyFilterGroup,
    root = false
  ): PolarisPropertyFilterGroup => {
    const group: any = {
      tokens: [],
      tokenGroups: [],
      operation: propertyFilterGroup.operation,
    };
    (propertyFilterGroup?.filters ?? []).forEach(
      (property: PropertyFilter | PropertyFilterGroup) => {
        if (property.type === "PropertyFilterGroup") {
          // If this nested group is an OR of same-property scalars for a
          // multi-select property (e.g. two `country=US` / `country=CA`
          // PropertyFilters), collapse it into a single Polaris enum
          // token so the UI shows the checkbox multi-select popover
          // instead of two separate chips.
          const enumToken = maybeCollapseToEnumToken(property);
          if (enumToken) {
            if (root) group.tokenGroups.push(enumToken);
            else group.tokens.push(enumToken);
            return;
          }
          if (root) {
            group.tokenGroups.push(
              transformPropertyFilterGroupToPolarisPropertyFilter(
                property,
                false
              )
            );
          } else {
            group.tokens.push(
              transformPropertyFilterGroupToPolarisPropertyFilter(
                property,
                false
              )
            );
          }
        } else if (property.type === "PropertyFilter") {
          // Single-value multi-select property: still emit an enum
          // token (one-element array) so Polaris renders the multi-
          // select popover in edit mode.
          if (MULTISELECT_PROPERTY_KEYS.has(property.propertyKey)) {
            const enumToken: PropertyFilterToken = {
              propertyKey: property.propertyKey,
              value: Array.isArray(property.value)
                ? property.value
                : [property.value],
              operator: "=",
            };
            if (root) group.tokenGroups.push(enumToken);
            else group.tokens.push(enumToken);
            return;
          }
          if (root) {
            group.tokenGroups.push(
              transformPropertyFilterToPolarisPropertyFilterToken(property)
            );
          } else {
            group.tokens.push(
              transformPropertyFilterToPolarisPropertyFilterToken(property)
            );
          }
        }
      }
    );
    return group;
  };

  /**
   * Return a single Polaris enum token if `group` is an OR of scalar
   * `PropertyFilter`s that all target the same multi-select property
   * (e.g. `country=US OR country=CA`). Returns `null` otherwise —
   * unrelated ORs (mixed properties, nested groups, non-enum
   * properties) fall through to normal per-token emission.
   */
  const maybeCollapseToEnumToken = (
    group: PropertyFilterGroup
  ): PropertyFilterToken | null => {
    if (group.operation !== "or") return null;
    const filters = group.filters ?? [];
    if (filters.length === 0) return null;
    const first = filters[0];
    if (first.type !== "PropertyFilter") return null;
    const key = first.propertyKey;
    if (!MULTISELECT_PROPERTY_KEYS.has(key)) return null;
    const allMatch = filters.every(
      (f) =>
        f.type === "PropertyFilter" &&
        (f as PropertyFilter).propertyKey === key &&
        (f as PropertyFilter).operator === "filter"
    );
    if (!allMatch) return null;
    // Dedupe values so `or(filter(k,v), filter(k,v))` (hand-crafted
    // URL or a nested Polaris token-group that repeats a value)
    // collapses to a single-value enum token — otherwise the
    // `format` joiner would render "v, v" in the chip and the
    // popover would show the same option preselected twice. Mirrors
    // the `seen`/`valueKey` dedup in `coalesceSamePropertyTokens`.
    const seen = new Set<string>();
    const values: any[] = [];
    for (const f of filters as PropertyFilter[]) {
      const vk =
        typeof f.value === "string" ? f.value : JSON.stringify(f.value);
      if (seen.has(vk)) continue;
      seen.add(vk);
      values.push(f.value);
    }
    return {
      propertyKey: key,
      value: values,
      operator: "=",
    };
  };

  const transformPolarisPropertyFilterTokenToPropertyFilter = (
    token: PropertyFilterToken
  ): PropertyFilter => ({
    type: "PropertyFilter",
    propertyKey: token?.propertyKey ?? "",
    value: token?.value ?? "",
    operator: token?.operator === ":" ? "fuzzy" : "filter",
  });

  const transformPropertyFilterToPolarisPropertyFilterToken = (
    property: PropertyFilter
  ): PropertyFilterToken => ({
    propertyKey: property?.propertyKey,
    value: property?.value ?? "",
    operator: property?.operator === "fuzzy" ? ":" : "=",
  });

  return (
    <div ref={containerRef}>
      <PropertyFilterComponent
        query={polarisPropertyFilter}
        onChange={({ detail }) => {
          // If free-text ASIN is on, promote property-less tokens then
          // split multi-value ASIN tokens into OR-joined chip groups.
          const source = { ...detail } as PolarisPropertyFilterGroup;
          const promoted = enableFreeTextAsin
            ? promoteFreeTextTokensToAsin(source)
            : source;
          const split = splitCommaSeparatedAsinTokens(promoted);
          const coalesced = coalesceSamePropertyTokens(split);
          const next = preProcessPolarisPropertyFilter(coalesced);
          setPolarisPropertyFilter(next);
          updateQueryFromPropertyFilters(next);
        }}
        expandToViewport
        i18nStrings={PROPERTY_FILTER_I18N}
        enableTokenGroups
        customGroupsText={customGroupsText}
        freeTextFiltering={{ operators: ["="], defaultOperator: "=" }}
        disableFreeTextFiltering={!enableFreeTextAsin}
        virtualScroll={true}
        filteringAriaLabel={
          enableFreeTextAsin
            ? "Filter by attribute or paste ASINs"
            : "Filter based on attribute values"
        }
        filteringPlaceholder={
          enableFreeTextAsin
            ? "Filter by attribute or paste ASINs (e.g. B004AT6YQN, B00CC8XHN9)"
            : "Filter based on attribute values"
        }
        filteringOptions={
          Object.entries(uiFilterableConfig)?.flatMap(
            ([_, property]) => property?.filteringOptions ?? []
          ) ?? []
        }
        filteringProperties={groupedFilteringProperties}
        {...(typeof tokenLimit === "number" ? { tokenLimit } : {})}
        {...(customControl ? { customControl } : {})}
        {...(customFilterActions ? { customFilterActions } : {})}
      />
    </div>
  );
};

export default PropertyFilterInput;
